/* The face's effect chain. A wave at level 3 or more rolls a random chain of components and
   compiles it. A modifier applies to everything on its right. The face shader then applies the
   compiled ops to the dots (render-state.ts packs them, dot-field.ts draws them).

   This started life as an untyped prototype; the port kept the face roll exactly the same. */

import { lerp } from "../math.js";

/** Each component's rolled parameters, tagged with its id. The tag ties the parameters to
    their component, so code can switch on it and never miss a key. */
export type ComponentParams =
  | { id: "paletteRoll"; d: number }
  | { id: "dropout"; frac: number }
  | { id: "dotScale"; k: number }
  | { id: "dotJitter"; px: number }
  | { id: "pixelate"; n: number }
  | { id: "amplify"; k: number }
  | { id: "temper"; k: number }
  | { id: "invert" }
  | { id: "crest"; t: number }
  | { id: "ghost"; a: number }
  | { id: "grayscale" };
export type ComponentId = ComponentParams["id"];

export interface ComponentDef {
  id: ComponentId;
  type: "OP" | "MOD" | "TRIG" | "PASSIVE";
  roll: (r: () => number) => ComponentParams;
}

/** A definition whose roll returns the params for its own id. */
function component<I extends ComponentId>(def: {
  id: I;
  type: ComponentDef["type"];
  roll: (r: () => number) => Extract<ComponentParams, { id: I }>;
}): ComponentDef {
  return def;
}

export interface ChainNode {
  type: ComponentDef["type"];
  id: ComponentId;
  def: ComponentDef;
  p: ComponentParams;
}

/* What a component inherits from the wrappers on its left:
   - `k`: the amplitude multiplier
   - `edge`: invert's flag, which moves the peak to the band's edges
   - `crest`: the face trigger's threshold */
interface Scope {
  k: number;
  edge: boolean;
  crest: number;
}

export interface CompiledOp extends Scope {
  p: ComponentParams;
}

interface Wrapper {
  p: ComponentParams;
  wk: number;
}

/* ---- Face ops. The canvas reads their rolled params (render-state.ts). ---- */
const FACE_OPS: readonly ComponentDef[] = [
  component({
    id: "paletteRoll",
    type: "OP",
    roll: (r) => ({ id: "paletteRoll", d: lerp(-40, 40, r()) }),
  }),
  component({
    id: "dropout",
    type: "OP",
    roll: (r) => ({ id: "dropout", frac: lerp(0.1, 0.4, r()) }),
  }),
  component({
    id: "dotScale",
    type: "OP",
    roll: (r) => ({ id: "dotScale", k: lerp(0.4, 1.8, r()) }),
  }),
  component({
    id: "dotJitter",
    type: "OP",
    roll: (r) => ({ id: "dotJitter", px: lerp(1, 5, r()) }),
  }),
  component({
    id: "pixelate",
    type: "OP",
    roll: (r) => ({ id: "pixelate", n: Math.round(lerp(2, 6, r())) }),
  }),
];

/* ---- Modifiers. `wrapScope` below works out what each one does to its targets. ---- */
const MODS: readonly ComponentDef[] = [
  component({
    id: "amplify",
    type: "MOD",
    roll: (r) => ({ id: "amplify", k: lerp(1.3, 2.2, r()) }),
  }),
  component({
    id: "temper",
    type: "MOD",
    roll: (r) => ({ id: "temper", k: lerp(0.4, 0.8, r()) }),
  }),
  component({ id: "invert", type: "MOD", roll: () => ({ id: "invert" }) }),
];

/* ---- Triggers. They gate the compiled amplitude, stored as flags on each op to their
   right; the face shader enforces them. ---- */
const TRIGGERS: readonly ComponentDef[] = [
  component({
    id: "crest",
    type: "TRIG",
    roll: (r) => ({ id: "crest", t: lerp(0.4, 0.7, r()) }),
  }),
];

/* ---- Passives. The face draws none of them, but each one takes up a chain slot, so they
   change how many ops and modifiers a roll gets. ---- */
const PASSIVES: readonly ComponentDef[] = [
  component({
    id: "ghost",
    type: "PASSIVE",
    roll: (r) => ({ id: "ghost", a: lerp(0.4, 0.85, r()) }),
  }),
  component({ id: "grayscale", type: "PASSIVE", roll: () => ({ id: "grayscale" }) }),
];

const ALL_COMPONENTS: readonly ComponentDef[] = [...FACE_OPS, ...MODS, ...TRIGGERS, ...PASSIVES];

/** A chain node with known params, for tests. */
export function chainNode(p: ComponentParams): ChainNode {
  const def = ALL_COMPONENTS.find((candidate) => candidate.id === p.id);
  if (!def) throw new Error(`face-chain: no component "${p.id}"`);
  return { type: def.type, id: p.id, def, p };
}

/* What a modifier or trigger does to the target on its right.
     s:  what the target has picked up so far (`{k, edge, crest}`)
     p:  this modifier's own rolled params (amplify's `k`, temper's `k`, …)
     wk: how much this modifier was itself amplified by the mods on its left, so an amplify
         before a temper makes the temper temper harder.
   Keeping the amplification it received (`s.k`) apart from the modifier's own tuning is
   what lets one modifier act on another. */
function wrapScope(s: Scope, p: ComponentParams, wk: number): Scope {
  switch (p.id) {
    case "amplify":
      return { ...s, k: s.k * (1 + (p.k - 1) * wk) };
    case "temper":
      // Amplification from the left can push the factor below zero. Stop at zero.
      return { ...s, k: s.k * Math.max(0, 1 - (1 - p.k) * wk) };
    case "invert":
      return { ...s, edge: !s.edge };
    case "crest":
      return { ...s, crest: Math.max(s.crest || 0, p.t) };
    default:
      return s;
  }
}

/** The shortest and longest chain, and the most ops in one chain. */
export const CAPS = { min: 2, max: 4, ops: 2 } as const;

const pick = <T>(arr: readonly T[], r: () => number): T => {
  const item = arr[Math.floor(r() * arr.length)];
  if (item === undefined) throw new Error("pick: called with an empty array");
  return item;
};

/* Rolls a chain within the caps. It draws components one at a time, throws out any that
   would break a cap, and stops once there's at least one op and either a limit or a coin
   flip says so. */
export function rollChain(rng: () => number = Math.random): ChainNode[] {
  const state: { chain: ChainNode[]; ops: number } = { chain: [], ops: 0 };

  const fits = (c: ChainNode): boolean => {
    if (state.chain.length >= CAPS.max) return false;
    return c.type !== "OP" || state.ops < CAPS.ops;
  };
  const add = (c: ChainNode): void => {
    state.chain.push(c);
    if (c.type === "OP") state.ops++;
  };
  const draw = (list: readonly ComponentDef[]): ChainNode => {
    const def = pick(list, rng);
    return { type: def.type, id: def.id, def, p: def.roll(rng) };
  };

  // At least one op first, then maybe some modifiers, triggers, and passives on top.
  let guard = 0;
  while (state.ops < 1 && guard++ < 50) {
    const c = draw(FACE_OPS);
    if (fits(c)) add(c);
  }

  while (state.chain.length < CAPS.max && guard++ < 200) {
    if (state.ops >= 1 && state.chain.length >= CAPS.min && rng() < 0.35) break;
    const bag = rng() < 0.5 ? FACE_OPS : rng() < 0.7 ? MODS : rng() < 0.85 ? TRIGGERS : PASSIVES;
    const c = draw(bag);
    if (fits(c)) add(c);
  }

  // Order matters (modifiers act rightward), but a raw roll tends to end in modifiers. A light
  // shuffle that keeps an op at the far right gives the modifiers something to bite.
  return orderChain(state.chain, rng);
}

function orderChain(chain: ChainNode[], r: () => number): ChainNode[] {
  const a = chain.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(r() * (i + 1));
    const ai = a[i];
    const aj = a[j];
    if (ai === undefined || aj === undefined) throw new Error("orderChain: index out of range");
    a[i] = aj;
    a[j] = ai;
  }
  /* A modifier or trigger only does something if an op sits to its right, so one at the end
     is dead weight: it uses a slot and does nothing. Swapping the rightmost op into the last
     non-passive slot makes sure every modifier has at least one op to work on. (Passives
     don't care where they sit, so they can go anywhere, last included.) */
  let lastOp = -1;
  for (let i = a.length - 1; i >= 0; i--) {
    const node = a[i];
    if (node !== undefined && node.type === "OP") {
      lastOp = i;
      break;
    }
  }
  if (lastOp >= 0) {
    let tailMod = a.length - 1;
    while (tailMod > lastOp) {
      const node = a[tailMod];
      if (node === undefined || node.type !== "PASSIVE") break;
      tailMod--;
    }
    if (tailMod > lastOp) {
      const lastNode = a[lastOp];
      const tailNode = a[tailMod];
      if (lastNode === undefined || tailNode === undefined)
        throw new Error("orderChain: index out of range");
      a[lastOp] = tailNode;
      a[tailMod] = lastNode;
    }
  }
  return a;
}

/* Compiles a chain: walks left to right and wraps each op in every modifier and trigger to
   its left. Passives compile to nothing. */
export function compile(chain: readonly ChainNode[]): CompiledOp[] {
  const wrappers: Wrapper[] = []; // `{ p, wk }`; `wk` is how much this mod was amplified
  const ops: CompiledOp[] = [];
  const NEUTRAL_SCOPE: Scope = { k: 1, edge: false, crest: 0 };

  // `wrapScope` only touches the `Scope` fields, so the scope can be worked out on its own
  // and the op's params added afterward.
  const throughScope = (base: Scope): Scope => {
    let s: Scope = base;
    for (const w of wrappers) s = wrapScope(s, w.p, w.wk);
    return s;
  };

  for (const c of chain) {
    if (c.type === "MOD" || c.type === "TRIG") {
      // Run the wrappers so far over a neutral scope to see how much this modifier gets
      // amplified itself: an amplify before a temper makes it temper harder.
      const self = throughScope({ ...NEUTRAL_SCOPE });
      wrappers.push({ p: c.p, wk: self.k });
      continue;
    }
    if (c.type === "PASSIVE") continue;
    const scope = throughScope({ ...NEUTRAL_SCOPE });
    ops.push({ p: c.p, ...scope });
  }
  return ops;
}
